import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useTenantInfo } from '../../contexts/TenantInfoContext';
import type { TenantInfo } from '../../resources/api.resource';

// Derive a union of only the boolean keys on TenantInfo so the flag prop is
// constrained to actual feature flags (showAppsButton, showChatButton, etc.).
type TenantFeatureFlag = {
  [K in keyof TenantInfo]: TenantInfo[K] extends boolean ? K : never;
}[keyof TenantInfo];

interface TenantFeatureRouteProps {
  flag: TenantFeatureFlag;
}

// Layout-route guard that blocks a subtree when the tenant has the given
// feature flag disabled. Mirrors LandingPage's `?? true` default so an unset
// flag stays enabled.
//
// On direct / new-tab navigation the tenant/me React Query starts cold, so
// tenantInfo is initially undefined. We wait for it to settle (data OR error)
// before deciding, ensuring the disabled-flag redirect is always based on real
// data and never on a race with the inflight fetch.
const TenantFeatureRoute = ({ flag }: TenantFeatureRouteProps) => {
  const { tenantInfo, isLoading, error } = useTenantInfo();

  // Still fetching — render nothing until the query settles.
  if (isLoading || (!tenantInfo && !error)) return null;

  const enabled = tenantInfo?.[flag] ?? true;
  if (!enabled) return <Navigate to="/landing" replace />;

  return <Outlet />;
};

export default TenantFeatureRoute;
